import type { IframeBridge } from '@hibro/platform-renderer';
import { type SurfaceHostedIn, surfaceInstanceId } from '@hibro/shared';

/** Must match `IFRAME_PORT_CHANNEL` in the preload / `@hibro/platform-main`. */
const IFRAME_PORT_CHANNEL = 'hibro:iframe-port';

/**
 * Fan a brokered port out to the `IframeSurface` registered for that view
 * *instance* (见 9 / §5.7).
 *
 * Keyed by instance id rather than view id: two documents of one view are two
 * iframes with two gated connections, and a view-id key would hand each port to
 * both of them.
 */
function createPortRegistry(): {
  deliver: (
    viewId: string,
    instanceKey: string | undefined,
    port: MessagePort,
  ) => void;
  onPort: IframeBridge['onPort'];
} {
  const handlers = new Map<string, Set<(port: MessagePort) => void>>();
  return {
    deliver: (viewId, instanceKey, port) => {
      for (const handler of handlers.get(
        surfaceInstanceId(viewId, instanceKey),
      ) ?? []) {
        handler(port);
      }
    },
    onPort: (viewId, instanceKey, handler) => {
      const key = surfaceInstanceId(viewId, instanceKey);
      let set = handlers.get(key);
      if (!set) {
        set = new Set();
        handlers.set(key, set);
      }
      set.add(handler);
      return () => {
        set.delete(handler);
        if (set.size === 0) {
          handlers.delete(key);
        }
      };
    },
  };
}

/**
 * Electron `IframeBridge` (见 9), over the preload `window.hibroIframe` IPC
 * bridge and the `window` message carrying the brokered gated `MessagePort`.
 * Lives in the app shell so platform-renderer stays free of the preload bridge;
 * shared by the workbench window and detached single-surface windows.
 *
 * The preload re-posts each gated port to the main world as a
 * `{ type: IFRAME_PORT_CHANNEL, viewId, instanceKey }` envelope with the
 * transferred port (it cannot cross the contextBridge). This bridge demuxes those
 * by instance to the matching `IframeSurface`, which forwards the port into its
 * iframe.
 */
export function createIframeBridge(): IframeBridge {
  const registry = createPortRegistry();

  window.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as {
      type?: string;
      viewId?: string;
      instanceKey?: string;
    } | null;
    if (data?.type !== IFRAME_PORT_CHANNEL || !data.viewId) {
      return;
    }
    const port = event.ports[0];
    if (port) {
      registry.deliver(data.viewId, data.instanceKey, port);
    }
  });

  return {
    connect: (viewId, instanceKey, hostedIn) =>
      window.hibroIframe?.connect(viewId, instanceKey, hostedIn),
    onPort: registry.onPort,
  };
}

/**
 * Browser `IframeBridge` (见 18). One hop shorter than the desktop path: with no
 * main process to broker through, the page mints the `MessageChannel` itself,
 * transfers one end to the SharedWorker host (which binds it as the view
 * instance's gated connection) and hands the other to the iframe.
 *
 * `brokerGatedPort` is injected rather than reaching for the worker here, so the
 * shared shell stays free of any SharedWorker specifics — the same reason the
 * Electron bridge above takes its port from the preload global.
 */
export function createWebIframeBridge(
  brokerGatedPort: (
    viewId: string,
    hostPort: MessagePort,
    instanceKey: string | undefined,
    hostedIn: SurfaceHostedIn,
  ) => void,
): IframeBridge {
  const registry = createPortRegistry();

  return {
    connect: (viewId, instanceKey, hostedIn) => {
      const channel = new MessageChannel();
      // `port1` goes to the host, `port2` to the iframe. `IframeSurface` holds
      // it until the iframe announces readiness, then transfers it in.
      brokerGatedPort(viewId, channel.port1, instanceKey, hostedIn);
      registry.deliver(viewId, instanceKey, channel.port2);
    },
    onPort: registry.onPort,
  };
}
